import React, { useState } from 'react';
import { Tag, X, Check, Loader2, Percent, DollarSign, Truck, Gift } from 'lucide-react';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CouponInput = ({ cartTotal, productIds, onApply, onRemove, appliedCoupon }) => {
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [codeType, setCodeType] = useState('coupon'); // 'coupon' or 'referral'

  const handleApply = async (e) => {
    e.preventDefault();
    if (!code.trim()) return;

    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      if (codeType === 'coupon') {
        // Validate coupon code
        const res = await axios.post(
          `${API_URL}/api/coupons/validate`,
          { code: code.trim(), cart_total: cartTotal, product_ids: productIds },
          { headers }
        );

        if (res.data.valid) {
          onApply({ ...res.data, code_type: 'coupon' });
          toast.success(`Coupon applied! ${res.data.coupon.description || ''}`);
          setCode('');
        }
      } else {
        // Validate referral code
        const res = await axios.post(
          `${API_URL}/api/referrals/validate`,
          { code: code.trim(), cart_total: cartTotal },
          { headers }
        );

        if (res.data.valid) {
          onApply({ ...res.data, code_type: 'referral' });
          toast.success(`Referral discount applied! ${res.data.discount_percent}% off`);
          setCode('');
        }
      }
    } catch (e) {
      const errorMsg = e.response?.data?.detail || `Invalid ${codeType} code`;
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = () => {
    onRemove();
    setCode('');
    setError(null);
  };

  const getDiscountIcon = (type) => {
    switch (type) {
      case 'percentage':
        return <Percent className="h-4 w-4" />;
      case 'fixed':
        return <DollarSign className="h-4 w-4" />;
      case 'free_shipping':
        return <Truck className="h-4 w-4" />;
      default:
        return <Tag className="h-4 w-4" />;
    }
  };

  if (appliedCoupon) {
    const isReferral = appliedCoupon.code_type === 'referral';
    return (
      <div className="bg-[#E0F2F1] rounded-xl p-4" data-testid="applied-coupon">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white ${isReferral ? 'bg-purple-600' : 'bg-[#3A5A40]'}`}>
              {isReferral ? <Gift className="h-5 w-5" /> : getDiscountIcon(appliedCoupon.coupon?.discount_type)}
            </div>
            <div>
              <p className="font-semibold text-[#1A1A1A]">
                {isReferral ? appliedCoupon.referral_code : appliedCoupon.coupon?.code}
              </p>
              <p className="text-sm text-[#3A5A40]">
                {isReferral 
                  ? `Referral: ${appliedCoupon.discount_percent}% off`
                  : `-$${appliedCoupon.discount_amount?.toFixed(2) || '0.00'} discount`
                }
              </p>
            </div>
          </div>
          <button
            onClick={handleRemove}
            className="p-2 hover:bg-white/50 rounded-lg transition-colors"
            data-testid="remove-coupon-btn"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>
        <p className="text-xs text-gray-500 mt-2">Only one code can be applied per order.</p>
      </div>
    );
  }

  return (
    <div data-testid="coupon-input">
      {/* Code Type Toggle */}
      <div className="flex gap-2 mb-3">
        <button
          type="button"
          onClick={() => setCodeType('coupon')}
          className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2 ${
            codeType === 'coupon' 
              ? 'bg-[#003456] text-white' 
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
        >
          <Tag className="h-4 w-4" />
          Coupon
        </button>
        <button
          type="button"
          onClick={() => setCodeType('referral')}
          className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2 ${
            codeType === 'referral' 
              ? 'bg-purple-600 text-white' 
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
        >
          <Gift className="h-4 w-4" />
          Referral
        </button>
      </div>
      
      <form onSubmit={handleApply} className="flex gap-2">
        <div className="flex-1 relative">
          {codeType === 'coupon' ? (
            <Tag className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          ) : (
            <Gift className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          )}
          <Input
            value={code}
            onChange={(e) => {
              setCode(e.target.value.toUpperCase());
              setError(null);
            }}
            placeholder={codeType === 'coupon' ? 'Enter coupon code' : 'Enter referral code'}
            className={`pl-10 ${error ? 'border-red-300' : ''}`}
            data-testid="coupon-code-input"
          />
        </div>
        <Button
          type="submit"
          disabled={loading || !code.trim()}
          variant="outline"
          className={`${codeType === 'coupon' ? 'border-[#3A5A40] text-[#3A5A40] hover:bg-[#3A5A40]' : 'border-purple-600 text-purple-600 hover:bg-purple-600'} hover:text-white`}
          data-testid="apply-coupon-btn"
        >
          {loading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            'Apply'
          )}
        </Button>
      </form>
      {error && (
        <p className="text-sm text-red-500 mt-2">{error}</p>
      )}
      <p className="text-xs text-gray-400 mt-2">You can use either a coupon or referral code, not both.</p>
    </div>
  );
};

export default CouponInput;
